import React from 'react';
import { useTranslation } from 'react-i18next';

import { closeIcon } from '@justgiving/icons';
import { Button } from '@justgiving/react-tracking';
import Svg from '@justgiving/svg';
import classNames from 'classnames';
import gql from 'graphql-tag';

import { EncourageShareOwnerFragment } from '../../../types/__generated__';
import { MODAL } from '../../context';
import { useApp } from '../../hooks';
import { usePageOwner } from '../../hooks';
import usePageQuery, {
  UsePageQueryResult,
  buildQuery,
} from '../../hooks/usePageQuery';
import isDesktopViewport from '../../utils/isDesktopViewport';
import IconButton from '../IconButton/IconButton';
import socialShareImage from './assets/social-share.svg';

import styles from './EncourageShareModal.scss';

interface EncourageShareModalProps {
  onClose: () => void;
}

const FRAGMENT = buildQuery(gql`
  fragment EncourageShareOwner on Page {
    owner {
      id
      name
      avatar
    }
  }
`);

export const EncourageShareModal: React.FunctionComponent<EncourageShareModalProps> = ({
  onClose,
}) => {
  const { isPageOwner } = usePageOwner();

  const result = usePageQuery<EncourageShareOwnerFragment>(FRAGMENT);
  const { t } = useTranslation('common', { useSuspense: false });

  const { showModal } = useApp();

  if (isPageOwner || !UsePageQueryResult.hasData(result) || !result.data) {
    return null;
  }

  const { owner } = result.data;
  const avatarUrl = owner?.avatar;
  const smallPrintMessage = isDesktopViewport()
    ? t('page.encourageShareModalSmallPrintDesktop')
    : t('page.encourageShareModalSmallPrintMobile');

  return (
    <div
      aria-label={t('page.encourageshareModal')}
      className={styles.wrapper}
      data-qa="share-modal"
    >
      <IconButton
        className={styles.close}
        onClick={onClose}
        title={t('page.closeEncourageShareModal')}
      >
        <Svg markup={closeIcon} />
      </IconButton>
      <div className={styles.image}>
        <img
          alt={t('page.encourageShareModalSocialShareImage')}
          className={classNames(styles.social, styles.avatar)}
          src={avatarUrl ?? socialShareImage}
        />
      </div>
      <div className={styles.description}>
        <h1 className={styles.heading}>{t('page.encourageShareModalTitle')}</h1>
        <div className={styles.message}>
          {t('page.encourageShareModalMessage', { pageOwner: owner?.name })}
        </div>
        <Button
          className={`jg-btn jg-btn--ghost`}
          onClick={() => showModal({ modal: MODAL.SHARE, overwrite: true })}
          type="button"
          trackingData={{
            action_name: 'click',
            subtype: 'share',
            event: 'click',
            event_value: 'share',
          }}
        >
          {t('common.share')}
        </Button>
        <p>
          <Button
            className={styles.later}
            onClick={onClose}
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              page_section: 'share with friends prompt',
              event: 'click',
              event_value: 'close',
            }}
          >
            {t('page.encourageShareModalLaterDismiss')}
          </Button>
        </p>
        <p className={styles.print}>{smallPrintMessage}</p>
      </div>
    </div>
  );
};
